import {
  VStack,
  MenuItem,
  useDisclosure,
  Flex,
  IconButton,
  Tooltip,
  Spacer,
} from '@chakra-ui/react';
import { WidgetWrapper } from '../shared/components/WidgetWrapper';
import { useTranslation } from 'react-i18next';
import { useSearchArticles } from '../../shared/search/useSearchArticles';
import { reactEvents } from '../../../../bridge/reactEvents';
import { maxExportArticlesAmount } from '../../searchArticles/SearchArticles';

import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { defaultIconSize, Icons } from '@texas/components/shared/Icons';
import { RowData, RowSelectionState } from '@tanstack/react-table';
import { useExportableArticles } from '@texas/components/searchArticles/useExportableArticles';
import { BaseWidgetProps } from '../widgets';
import { SearchArticleOptions } from '../shared/types';
import { DataTableContainerFooter } from '@texas/components/shared/dataTable/DataTableContainerFooter';
import { DataTableContainer } from '@texas/components/shared/dataTable/DataTableContainer';
import { WidgetType } from '../shared/types';
import { searchApi } from '@texas/api/endpoints/search/searchApi';
import { emptyPagedItems } from '@texas/utils/helpers/commonHelpers';
import { ArticlesFilterModal } from '../shared/components/articles/ArticlesFilterModal';
import { ArticlesCardView } from '@texas/components/shared/views/ArticlesCardView';
import { ArticleSearchView } from '@texas/components/searchArticles/types';
import { ArticlesPrinterFilter } from '../shared/components/articles/ArticlesFilterPrinter';
import { useActiveContext } from '@texas/hooks/useActiveContext';
import { SearchArticleContext } from './context';
import { variantsApi } from '@texas/api/endpoints/variantsApi';
import { TagFilterProvider } from '@texas/components/shared/tags/TagFilterContext';
import {
  excelExportFileName,
  exportPrefixes,
  saveBlobAsFile,
} from '@texas/utils/helpers/filesHelper';
import { defaultEmptyFilters } from '@texas/api/endpoints/search/searchVariantsTypes';

interface Props extends BaseWidgetProps {
  options: SearchArticleOptions;
}

export function ArticlesWidget({
  options,
  customTitle,
  widgetId,
  color,
}: Props) {
  const { t } = useTranslation();

  const search = useSearchArticles({
    defaultFilters: defaultEmptyFilters,
    defaultArticleSearchProps: options.defaultSearchPage,
    defaultPage: options.defaultSearchPage.searchParams.page,
    localStorageKey: options.localStorageKey
      ? `${options.localStorageKey}_${widgetId}`
      : undefined,
    filterOptions: options.filterOptions,
    limit: options.pageSize,
    request: searchApi.searchVariants,
    defaultData: { pagedItems: emptyPagedItems },
  });

  const { articles, searchPage, loading } = search;

  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const { exportableArticles, setExportableArticles } = useExportableArticles(
    articles?.exportArticleItems ?? [],
  );
  useEffect(
    () => setExportableArticles(selectedRows),
    [selectedRows, setExportableArticles],
  );

  const {
    isOpen: isFiltersOpen,
    onClose: onFiltersClosed,
    onOpen: onFiltersOpen,
  } = useDisclosure();

  return (
    <>
      <SearchArticleContext.Provider value={search}>
        <WidgetWrapper
          toolbox={
            <WidgetToolbox options={options} onFiltersOpen={onFiltersOpen} />
          }
          widgetId={widgetId}
          loading={loading}
          settings={options.componentSettings}
          customColor={color}
          type={WidgetType.Articles}
          style="default"
          header={customTitle ?? t('widgets.articles.articles')}
          extendedMenu={
            <>
              <MenuItem
                onClick={async () => {
                  const response = await variantsApi.downloadExcel(
                    articles?.exportArticleItems.flatMap((x) => {
                      return x.branches.flatMap((b) => ({
                        variantId: x.variantId,
                        branchId: b.id,
                      }));
                    }) ?? [],
                  );

                  if (response.hasError) return;

                  saveBlobAsFile(
                    response.data,
                    excelExportFileName(exportPrefixes.articles),
                  );
                }}
                isDisabled={articles?.pagedItems.totalItems == 0}
                icon={<Icons.FileExcelBox boxSize={defaultIconSize} />}
              >
                {t('general.exportExcel')}
              </MenuItem>
              <MenuItem
                onClick={async () => {
                  if (exportableArticles.current.length === 0) return;

                  const response = await variantsApi.downloadExcel(
                    exportableArticles.current.flatMap((x) => {
                      return x.branches.flatMap((b) => ({
                        variantId: x.variantId,
                        branchId: b.id,
                      }));
                    }),
                  );

                  if (response.hasError) return;

                  saveBlobAsFile(
                    response.data,
                    excelExportFileName(exportPrefixes.articles),
                  );
                }}
                isDisabled={Object.keys(selectedRows).length === 0}
                icon={<Icons.FileExcelBox boxSize={defaultIconSize} />}
              >
                {t('general.exportExcelSelected')}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  reactEvents.excelExportClicked.dispatch({
                    articles: exportableArticles.current,
                    branchIds: searchPage.searchParams['branchIds'] ?? [],
                    hideBranchIds: false,
                  });
                }}
                isDisabled={
                  Object.keys(selectedRows).length === 0 ||
                  Object.keys(selectedRows).length > maxExportArticlesAmount
                }
                icon={<Icons.FileExport boxSize={defaultIconSize} />}
              >
                {t('general.exportExcelOld')}
              </MenuItem>
            </>
          }
        >
          <VStack h="100%" w="100%" align="start">
            <InnerWidget
              widgetId={widgetId}
              options={options}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
            />
          </VStack>
        </WidgetWrapper>
        <ArticlesFilterModal
          search={search}
          widgetRenderComponent={
            <InnerWidget
              overrideView="table"
              widgetId={widgetId}
              options={options}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
            />
          }
          isOpen={isFiltersOpen}
          onClose={onFiltersClosed}
        />
      </SearchArticleContext.Provider>
    </>
  );
}

function InnerWidget({
  options,
  widgetId,
  selectedRows,
  setSelectedRows,
  overrideView,
}: {
  options: SearchArticleOptions;
  widgetId: string;
  selectedRows: RowSelectionState;
  setSelectedRows: Dispatch<SetStateAction<RowSelectionState>>;
  overrideView?: ArticleSearchView;
}) {
  const { t } = useTranslation();
  const columns = useMemo(() => {
    return options.columns(t);
  }, [options, t]);

  const search = useActiveContext(SearchArticleContext);
  const {
    setSearchParamsWithRef,
    searchParamsRef,
    searchPage,
    articles,
    loading,
    error,
    view,
  } = search;

  return (
    <TagFilterProvider>
      <ArticlesPrinterFilter search={search} />
      <DataTableContainer
        hidden={(overrideView ?? view) !== 'table'}
        p={4}
        w="100%"
        minH={0}
        display="flex"
        flexDir="column"
        error={error}
        css={{ ' th, td': { whiteSpace: 'normal' } }}
        tableId={
          options.persistColumnVisibility !== false
            ? `articles-widget-${widgetId}`
            : undefined
        }
        datatable={{
          data: articles?.pagedItems.items ?? [],
          columns: columns,
          isLoading: loading,
          selectionProps: {
            setSelectedRows: setSelectedRows,
            selectedRows: selectedRows,
          },
          rowProps: {
            getRowId: (originalRow, _) => {
              const rowData: RowData = {
                articleId: originalRow.articleId,
              };
              return JSON.stringify(rowData);
            },
          },
          sorted: {
            onSortedChange: ({ key, desc }) => {
              setSearchParamsWithRef({
                ...searchParamsRef.current,
                sortBy: key,
                sortDesc: desc,
                page: options.defaultSearchPage.searchParams.page,
              });
            },
            key: searchPage.searchParams['sortBy'],
            desc: searchPage.searchParams['sortDesc'],
          },
        }}
        pagination={{
          totalItems: articles?.pagedItems.totalItems ?? 0,
          pageSize: options.pageSize,
          currentPage: searchPage.searchParams.page,
          onPageChange: (page) =>
            setSearchParamsWithRef({
              ...searchParamsRef.current,
              page,
            }),
        }}
        footer={
          <DataTableContainerFooter
            selectedRows={Object.keys(selectedRows).length}
            onClear={() => setSelectedRows({})}
            text={t('searchArticles.articlesSelected')}
          />
        }
      />
      <Flex
        hidden={(overrideView ?? view) !== 'card'}
        w="100%"
        direction="column"
        overflowX="auto"
        overflowY="hidden"
        pb={2}
      >
        <ArticlesCardView
          articles={articles}
          searchPage={searchPage}
          searchParamsRef={searchParamsRef}
          setSearchParamsWithRef={setSearchParamsWithRef}
          error={error}
          loading={loading}
        />
      </Flex>
    </TagFilterProvider>
  );
}

function WidgetToolbox({
  options,
  onFiltersOpen,
}: {
  options: SearchArticleOptions;
  onFiltersOpen: () => void;
}) {
  const { t } = useTranslation();
  const { view, handleViewChange } = useActiveContext(SearchArticleContext);
  return (
    <Flex gap={2}>
      <Tooltip label={t('view.tableView')}>
        <IconButton
          variant={view === 'table' ? 'texas-solid' : 'texas-light'}
          size="sm"
          icon={<Icons.ViewTable />}
          aria-label={t('view.tableView')}
          onClick={() => handleViewChange('table')}
        />
      </Tooltip>
      <Tooltip label={t('view.cardView')}>
        <IconButton
          variant={view === 'card' ? 'texas-solid' : 'texas-light'}
          size="sm"
          icon={<Icons.ViewCard />}
          aria-label={t('view.cardView')}
          onClick={() => handleViewChange('card')}
        />
      </Tooltip>
      {options.componentSettings?.editFilters !== 'hidden' && (
        <>
          <Spacer />
          <Tooltip label={t('widgets.changeFilters')}>
            <IconButton
              size="sm"
              variant="texas-light"
              icon={<Icons.FilterVariant />}
              aria-label={t('widgets.changeFilters')}
              onClick={() => onFiltersOpen()}
            />
          </Tooltip>
        </>
      )}
    </Flex>
  );
}
